/** Import types. */
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import type {TimeSeries, TransactionSettings} from "../../base/ui_general/GeneralInterfaces.tsx";
import {
    BaseComponent,
    type BaseComponentState,
    BaseComponentStateInitial
} from "../../base/ui_general/BaseComponent.tsx";
import {InputStateControl} from "../../base/utils/InputStateControl.tsx";
import autobind from "autobind-decorator";
import {
    ApiEndPoints,
    DayCountConventionEnum,
    LabelTextMessageTypes,
    LossDefinitionEnum,
    RiskPercentileEnum,
    TextfieldInputTypes,
    ValidationSchemes
} from "../../base/Settings.tsx";
import {formatPercent, getLabelTextMessage} from "../../base/utils/GeneralUtilities.tsx";
import {Col, Row} from "react-bootstrap";
import {RBSDropdown} from "../../base/rbs_components/RBSDropdown.tsx";
import {RBSTextField} from "../../base/rbs_components/RBSTextField.tsx";
import {getRequest_update_transaction_settings} from "../../api/transaction_settings";
import styles from './TabShared.module.css';
import {RBSCard, RBSCardVariants} from "../../base/rbs_components/RBSCard.tsx";
import {TabCardHeader} from './components/TabCardHeader';


/** Props for the SettingsTab component. */
interface SettingsTabProps extends DesignCenterTabProps {
    /** Transaction settings object */
    transaction_settings: TransactionSettings | undefined;
    /** List of time series available for selection. */
    time_series: Array<TimeSeries>;
}


/** State for the SettingsTab component. */
interface SettingsTabState extends BaseComponentState {
    transactionSettingValid: boolean
}

/**
 * Displays an overview of transaction settings.
 */
export class SettingsTab extends BaseComponent<SettingsTabProps, SettingsTabState> {
    private readonly inputStateControl: InputStateControl;

    constructor(props: SettingsTabProps) {
        super(props);

        this.state = {
            ...BaseComponentStateInitial,
            transactionSettingValid: true,
        };

        this.inputStateControl = new InputStateControl();
        this.inputStateControl.setGlobalValidityUpdateCallback(this.handleTransactionSettingValidityUpdate);
    }


    /**
     * Updates the validity state of the Portfolio modal.
     * Triggered by the Portfolio InputStateControl.
     */
    @autobind
    handleTransactionSettingValidityUpdate(isValid: boolean) {
        this.setState({transactionSettingValid: isValid});
    }


    /**
     * Handles updates to any field in the settings tab and sends the update to the backend.
     */
    @autobind
    handleSettingsChange() {
        if (!this.props.transaction_settings || !this.inputStateControl.isValidOverall()) return;
        const number_of_simulations = this.inputStateControl.getStringValueForKey("number_of_simulations");
        const updatedSettings: TransactionSettings = {
            ...this.props.transaction_settings,
            number_of_simulations: number_of_simulations ? Number(number_of_simulations) : this.props.transaction_settings.number_of_simulations,
            discounting_curve_id: this.inputStateControl.getStringValueForKey("discounting_curve_id") ?? this.props.transaction_settings.discounting_curve_id,
            loss_definition: this.inputStateControl.getStringValueForKey("loss_definition") as LossDefinitionEnum ?? this.props.transaction_settings.loss_definition,
            day_count_convention: this.inputStateControl.getStringValueForKey("day_count_convention") as DayCountConventionEnum ?? this.props.transaction_settings.day_count_convention,
            risk_percentile: Number(this.inputStateControl.getStringValueForKey("risk_percentile") ?? this.props.transaction_settings.risk_percentile),
        };
        this.processFetchRequest(
            getRequest_update_transaction_settings(updatedSettings),
            () => {
                this.props.loadData([ApiEndPoints.TRANSACTION_SETTINGS]);
                this.displaySuccess(getLabelTextMessage(LabelTextMessageTypes.TRANSACTION_SETTINGS_UPDATED, this.props.user_language));
            }
        );
    }


    render() {
        if (!this.props.transaction_settings) {
            return <div>{getLabelTextMessage(LabelTextMessageTypes.NO_TRANSACTION_LOADED, this.props.user_language)}</div>;
        }

        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea}>
                    <RBSCard
                        header={
                            <TabCardHeader
                                icon={"⚙️"}
                                title={<span className={styles.cardHeaderTitle}>Transaction Settings</span>}
                            />
                        }
                        header_class_name={styles.cardHeaderPrimary}
                        background={RBSCardVariants.Light}
                        border={RBSCardVariants.Primary}
                        body_class_name={styles.cardBodyPanel}
                        container_class_name={styles.roundedCard}
                    >
                        <Row className="mb-4">
                            <Col md={4} className="mb-3 mb-md-0">
                                <RBSTextField
                                    label={getLabelTextMessage(LabelTextMessageTypes.NUMBER_OF_SIMULATIONS, this.props.user_language)}
                                    input_key="number_of_simulations"
                                    prefill={this.props.transaction_settings.number_of_simulations.toString()}
                                    validation_schemes={[
                                        ValidationSchemes.REQUIRED,
                                        ValidationSchemes.NUMBER,
                                        ValidationSchemes.INTEGER,
                                        ValidationSchemes.MIN_1
                                    ]}
                                    input_state_control={this.inputStateControl}
                                    type={TextfieldInputTypes.NUMBER}
                                    onChange={this.handleSettingsChange}
                                />
                            </Col>
                            <Col md={4} className="mb-3 mb-md-0">
                                <RBSDropdown
                                    label={getLabelTextMessage(LabelTextMessageTypes.DISCOUNTING_CURVE_ID, this.props.user_language)}
                                    input_key="discounting_curve_id"
                                    prefill={this.props.transaction_settings.discounting_curve_id}
                                    items={[
                                        {label: "---", value: ""},
                                        ...(this.props.time_series.map(
                                            ts => ({label: ts.name, value: ts.id})
                                        ))
                                    ]}
                                    input_state_control={this.inputStateControl}
                                    validation_schemes={[ValidationSchemes.REQUIRED]}
                                    onChange={this.handleSettingsChange}
                                />
                            </Col>
                            <Col md={4}>
                                <RBSDropdown
                                    label={getLabelTextMessage(LabelTextMessageTypes.LOSS_DEFINITION, this.props.user_language)}
                                    input_key="loss_definition"
                                    prefill={this.props.transaction_settings.loss_definition}
                                    items={Object.values(LossDefinitionEnum).map(dd => ({label: dd, value: dd}))}
                                    input_state_control={this.inputStateControl}
                                    validation_schemes={[ValidationSchemes.REQUIRED]}
                                    onChange={this.handleSettingsChange}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4} className="mb-3 mb-md-0">
                                <RBSDropdown
                                    label={getLabelTextMessage(LabelTextMessageTypes.DAY_COUNT_CONVENTION, this.props.user_language)}
                                    input_key="day_count_convention"
                                    prefill={this.props.transaction_settings.day_count_convention}
                                    items={Object.values(DayCountConventionEnum).map(dd => ({label: dd, value: dd}))}
                                    input_state_control={this.inputStateControl}
                                    validation_schemes={[ValidationSchemes.REQUIRED]}
                                    onChange={this.handleSettingsChange}
                                />
                            </Col>
                            <Col md={4}>
                                <RBSDropdown
                                    label={getLabelTextMessage(LabelTextMessageTypes.RISK_PERCENTILE, this.props.user_language)}
                                    input_key="risk_percentile"
                                    prefill={this.props.transaction_settings.risk_percentile.toString()}
                                    items={Object.values(RiskPercentileEnum)
                                        .filter((v) => typeof v === "number")
                                        .map((dd) => ({label: formatPercent(dd), value: dd.toString()}))}
                                    input_state_control={this.inputStateControl}
                                    validation_schemes={[ValidationSchemes.REQUIRED]}
                                    onChange={this.handleSettingsChange}
                                />
                            </Col>
                        </Row>
                    </RBSCard>
                </div>
            </div>
        );
    }
}
